import {
    type CSSProperties,
    type ReactNode,
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    type IToggleGroupProps,
    ToggleGroup
} from "ncore-ui-kit";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Caption,
    Preview,
    Section
} from "../../components/content";
import {
    StarIcon,
    BellIcon,
    UserIcon
} from "../../anatomy/icons";

const NAME = "ToggleGroup";

const USAGE_CODE = `const [range, setRange] = useState("week");

const handleChange = (value: string | Array<string>) => {
    setRange(typeof value === "string" ? value : "");
};

<ToggleGroup
    attachBehaviour="attached"
    onValueChange={handleChange}
    title="Time range"
    variant="outline"
    value={range}
>
    <ToggleGroup.Item
        title="Day"
        value="day"
    />
    <ToggleGroup.Item
        title="Week"
        value="week"
    />
    <ToggleGroup.Item
        title="Month"
        value="month"
    />
</ToggleGroup>`;

const SELECTION_CODE = `const [filters, setFilters] = useState<Array<string>>([
    "starred"
]);

const handleChange = (value: string | Array<string>) => {
    setFilters(Array.isArray(value) ? value : []);
};

<ToggleGroup
    selectBehaviour="multiple"
    onValueChange={handleChange}
    title="Filters"
    value={filters}
>
    <ToggleGroup.Item
        icon={StarIcon}
        title="Starred"
        value="starred"
    />
    <ToggleGroup.Item
        icon={BellIcon}
        title="Unread"
        value="unread"
    />
</ToggleGroup>`;

const SIZES = [
    "small",
    "medium",
    "large"
] as const;

const DEMO_ITEM_STYLE: CSSProperties = {
    flexDirection: "column",
    alignItems: "center",
    display: "flex"
};

const DEMO_CONTENT_STYLE: CSSProperties = {
    justifyContent: "center",
    display: "flex",
    width: "100%"
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("toggleGroupPage.selectionTitle"),
        id: "selection"
    },
    {
        title: t("toggleGroupPage.appearanceTitle"),
        id: "appearance"
    },
    {
        title: t("section.sizes"),
        id: "sizes"
    },
    {
        title: t("toggleGroupPage.orientationTitle"),
        id: "orientation"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const DemoItem = ({
    children,
    label
}: {
    children: ReactNode;
    label: string;
}) => <div
    style={DEMO_ITEM_STYLE}
>
    <div
        style={DEMO_CONTENT_STYLE}
    >
        {children}
    </div>
    <Caption>
        {label}
    </Caption>
</div>;

const ToggleGroupPage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        range,
        setRange
    ] = useState("week");

    const [
        filters,
        setFilters
    ] = useState<Array<string>>([
        "starred"
    ]);

    const rangeLabels: Record<string, string> = {
        month: t("toggleGroupPage.month"),
        week: t("toggleGroupPage.week"),
        day: t("toggleGroupPage.day")
    };

    const handleRangeChange = (value: string | Array<string>) => {
        setRange(typeof value === "string" ? value : "");
    };

    const handleFiltersChange = (value: string | Array<string>) => {
        setFilters(Array.isArray(value) ? value : []);
    };

    const renderRange = (props: Pick<IToggleGroupProps, "attachBehaviour" | "isClearEnabled" | "isDisabled" | "variant" | "size">, isMonthDisabled = false) => <ToggleGroup
        title={t("toggleGroupPage.rangeTitle")}
        defaultValue="week"
        {...props}
    >
        <ToggleGroup.Item
            title={t("toggleGroupPage.day")}
            value="day"
        />
        <ToggleGroup.Item
            title={t("toggleGroupPage.week")}
            value="week"
        />
        <ToggleGroup.Item
            title={t("toggleGroupPage.month")}
            isDisabled={isMonthDisabled}
            value="month"
        />
    </ToggleGroup>;

    const renderFilters = (props: Pick<IToggleGroupProps, "attachBehaviour" | "orientation" | "variant" | "value" | "onValueChange">) => <ToggleGroup
        title={t("toggleGroupPage.filtersTitle")}
        selectBehaviour="multiple"
        {...props}
    >
        <ToggleGroup.Item
            title={t("toggleGroupPage.starred")}
            icon={StarIcon}
            value="starred"
        />
        <ToggleGroup.Item
            title={t("toggleGroupPage.unread")}
            icon={BellIcon}
            value="unread"
        />
        <ToggleGroup.Item
            title={t("toggleGroupPage.mine")}
            icon={UserIcon}
            value="mine"
        />
    </ToggleGroup>;

    return <Article>
        <ComponentHeader
            lead={t("toggleGroupPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("toggleGroupPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("toggleGroupPage.overviewCaption", [
                    rangeLabels[range] ?? t("toggleGroupPage.none")
                ])}
            >
                <ToggleGroup
                    title={t("toggleGroupPage.rangeTitle")}
                    onValueChange={handleRangeChange}
                    attachBehaviour="attached"
                    variant="outline"
                    value={range}
                >
                    <ToggleGroup.Item
                        title={t("toggleGroupPage.day")}
                        value="day"
                    />
                    <ToggleGroup.Item
                        title={t("toggleGroupPage.week")}
                        value="week"
                    />
                    <ToggleGroup.Item
                        title={t("toggleGroupPage.month")}
                        value="month"
                    />
                </ToggleGroup>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("toggleGroupPage.selectionTitle")}
            id="selection"
        >
            <Paragraph>
                {t("toggleGroupPage.selectionText")}
            </Paragraph>
            <Preview>
                <DemoItem
                    label="single · isClearEnabled={false}"
                >
                    {
                        renderRange({
                            attachBehaviour: "attached",
                            isClearEnabled: false,
                            variant: "outline"
                        })
                    }
                </DemoItem>
                <DemoItem
                    label={`multiple · ${JSON.stringify(filters)}`}
                >
                    {
                        renderFilters({
                            onValueChange: handleFiltersChange,
                            attachBehaviour: "attached",
                            variant: "outline",
                            value: filters
                        })
                    }
                </DemoItem>
            </Preview>
            <CodeBlock
                code={SELECTION_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("toggleGroupPage.appearanceTitle")}
            id="appearance"
        >
            <Paragraph>
                {t("toggleGroupPage.appearanceText")}
            </Paragraph>
            <Preview>
                <DemoItem
                    label="ghost"
                >
                    {
                        renderRange({
                            variant: "ghost"
                        })
                    }
                </DemoItem>
                <DemoItem
                    label="outline"
                >
                    {
                        renderRange({
                            variant: "outline"
                        })
                    }
                </DemoItem>
                <DemoItem
                    label="outline · attached"
                >
                    {
                        renderRange({
                            attachBehaviour: "attached",
                            variant: "outline"
                        })
                    }
                </DemoItem>
            </Preview>
        </Section>
        <Section
            title={t("section.sizes")}
            id="sizes"
        >
            <Paragraph>
                {t("toggleGroupPage.sizesText")}
            </Paragraph>
            <Preview>
                {
                    SIZES.map((size) => <DemoItem
                        label={size}
                        key={size}
                    >
                        {
                            renderRange({
                                attachBehaviour: "attached",
                                variant: "outline",
                                size
                            })
                        }
                    </DemoItem>)
                }
            </Preview>
        </Section>
        <Section
            title={t("toggleGroupPage.orientationTitle")}
            id="orientation"
        >
            <Paragraph>
                {t("toggleGroupPage.orientationText")}
            </Paragraph>
            <Preview>
                <DemoItem
                    label="vertical"
                >
                    {
                        renderFilters({
                            orientation: "vertical"
                        })
                    }
                </DemoItem>
                <DemoItem
                    label="vertical · attached"
                >
                    {
                        renderFilters({
                            attachBehaviour: "attached",
                            orientation: "vertical",
                            variant: "outline"
                        })
                    }
                </DemoItem>
            </Preview>
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("toggleGroupPage.statesText")}
            </Paragraph>
            <Preview>
                <DemoItem
                    label={t("toggleGroupPage.disabledGroup")}
                >
                    {
                        renderRange({
                            attachBehaviour: "attached",
                            variant: "outline",
                            isDisabled: true
                        })
                    }
                </DemoItem>
                <DemoItem
                    label={t("toggleGroupPage.disabledItem")}
                >
                    {
                        renderRange({
                            attachBehaviour: "attached",
                            variant: "outline"
                        }, true)
                    }
                </DemoItem>
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("toggleGroupPage.a11yRole"),
                    t("toggleGroupPage.a11yTabStop"),
                    t("toggleGroupPage.a11yArrows"),
                    t("toggleGroupPage.a11yDisabled"),
                    t("toggleGroupPage.a11yIconOnly"),
                    t("toggleGroupPage.a11yWarnings")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("toggleGroupPage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("toggleGroupPage.propSelectBehaviour"),
                        type: "\"single\" | \"multiple\"",
                        defaultValue: "\"single\"",
                        name: "selectBehaviour"
                    },
                    {
                        description: t("toggleGroupPage.propValue"),
                        type: "string | Array<string>",
                        name: "value"
                    },
                    {
                        description: t("toggleGroupPage.propDefaultValue"),
                        type: "string | Array<string>",
                        name: "defaultValue"
                    },
                    {
                        description: t("toggleGroupPage.propOnValueChange"),
                        type: "(value: string | Array<string>) => void",
                        name: "onValueChange"
                    },
                    {
                        description: t("toggleGroupPage.propIsClearEnabled"),
                        name: "isClearEnabled",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("toggleGroupPage.propVariant"),
                        type: "\"ghost\" | \"outline\"",
                        defaultValue: "\"ghost\"",
                        name: "variant"
                    },
                    {
                        description: t("toggleGroupPage.propAttachBehaviour"),
                        type: "\"separate\" | \"attached\"",
                        defaultValue: "\"separate\"",
                        name: "attachBehaviour"
                    },
                    {
                        description: t("toggleGroupPage.propSpreadBehaviour"),
                        type: "\"baseline\" | \"stretch\"",
                        defaultValue: "\"baseline\"",
                        name: "spreadBehaviour"
                    },
                    {
                        description: t("toggleGroupPage.propOrientation"),
                        type: "\"horizontal\" | \"vertical\"",
                        defaultValue: "\"horizontal\"",
                        name: "orientation"
                    },
                    {
                        description: t("toggleGroupPage.propSize"),
                        type: "\"small\" | \"medium\" | \"large\"",
                        name: "size"
                    },
                    {
                        description: t("toggleGroupPage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("toggleGroupPage.propIsPressScaleEnabled"),
                        name: "isPressScaleEnabled",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("toggleGroupPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("toggleGroupPage.propChildren"),
                        type: "ReactNode",
                        name: "children"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("toggleGroupPage.propsNote")}
            </Paragraph>
            <Paragraph>
                {t("toggleGroupPage.itemPropsText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("toggleGroupPage.propItemValue"),
                        type: "string",
                        name: "value"
                    },
                    {
                        description: t("toggleGroupPage.propItemTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("toggleGroupPage.propItemIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    },
                    {
                        description: t("toggleGroupPage.propItemActiveIcon"),
                        type: "NCoreUIKitIcon",
                        name: "activeIcon"
                    },
                    {
                        description: t("toggleGroupPage.propItemIsIconOnly"),
                        defaultValue: "false",
                        name: "isIconOnly",
                        type: "boolean"
                    },
                    {
                        description: t("toggleGroupPage.propItemIconDirection"),
                        type: "\"start\" | \"end\"",
                        defaultValue: "\"start\"",
                        name: "iconDirection"
                    },
                    {
                        description: t("toggleGroupPage.propItemIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("toggleGroupPage.propItemCustomTheme"),
                        name: "customTheme"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("toggleGroupPage.itemNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default ToggleGroupPage;
